import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { validators, formats, dates } from 'investira.sdk';
import { IconButton, Tooltip, Stack, Icon, Chip, LoadingButton } from 'investira.react.components';
import { BulkContextActions, DataTable } from '../';
import usePerfil from '../../../hooks/usePerfil';
import helpers from '../../../utils/helpers';
import {
  canEditAvaliacao,
  canViewAvaliacao,
  getAvaliacaoStatusColor,
  getAvaliacaoStatusDescricao,
  isAvaliacaoRascunhoStatus,
  isAvaliacaoDoneStatus,
  isAvaliacaoWaitingStatus
} from '../../../pages/Avaliacoes/_shared/avaliacaoStatus';
import withMessage from '../../../hoc/withMessage';
import services from '../../../services';

const ResendButton = withMessage(props => {
  const { avaliacao_id } = props.data;
  const [isSubmitting, setIsSubmitting] = useState(false);
  function handleSendEmail() {
    setIsSubmitting(true);
    services.avaliacoes.enviar(
      avaliacao_id,
      () => {
        props.onMessageSuccess(`Email enviado com sucesso`);
      },
      () => {
        props.onMessageError(`Não foi possível enviar o email neste momento.`);
      },
      () => {
        setIsSubmitting(false);
      }
    );
  }
  return (
    <LoadingButton
      variant="contained"
      color="primary"
      // startIcon={<Icon iconName="send" size={16} />}
      size="small"
      sx={{
        px: 1,
        whiteSpace: 'nowrap'
      }}
      type="submit"
      // disabled={}
      loading={isSubmitting}
      onClick={handleSendEmail}>
      Reenviar email
    </LoadingButton>
  );
});

// Presets temporários enquanto o endpoint não retorna as notas e performances processadas.
const wConjuntosMock = {
  BROIT1CTF005: {
    notas: { notaQualitativa: 2.8, notaQuantitativa: 2.6 },
    performance: {
      notasGestor: [2.7, 2.5, 2.8],
      notasEntidade: [2.9, 2.7, 2.85]
    },
    resumo: {
      desempenhoQuantitativo: 'forte',
      periodoMeses: 36,
      rankingPosicao: 4,
      rankingTotal: 120,
      capacidadeFiduciaria: 'forte',
      garantiaConflitos: 'plena',
      atendimentoRegulamento: 'plenamente'
    }
  },
  BRLAW1CTF001: {
    notas: { notaQualitativa: 2.15, notaQuantitativa: 1.9 },
    performance: {
      notasGestor: [2, 1.8, 2.1],
      notasEntidade: [2.2, 2, 2.05]
    },
    resumo: {
      desempenhoQuantitativo: 'modesto',
      periodoMeses: 24,
      rankingPosicao: 32,
      rankingTotal: 120,
      capacidadeFiduciaria: 'modesta',
      garantiaConflitos: 'razoável',
      atendimentoRegulamento: 'razoavelmente'
    }
  },
  BRSPX2CTF004: {
    notas: { notaQualitativa: 1.35, notaQuantitativa: 1.6 },
    performance: {
      notasGestor: [1.3, 1.5, 1.4],
      notasEntidade: [1.4, 1.2, 1.55]
    },
    resumo: {
      desempenhoQuantitativo: 'fraco',
      periodoMeses: 12,
      rankingPosicao: 84,
      rankingTotal: 120,
      capacidadeFiduciaria: 'fraca',
      garantiaConflitos: 'baixa',
      atendimentoRegulamento: 'insatisfatoriamente'
    }
  },
  generico: {
    notas: { notaQualitativa: 2.5, notaQuantitativa: 2 },
    performance: {
      notasGestor: [2.4, 2.1, 2.3],
      notasEntidade: [2.6, 2.3, 2.5]
    },
    resumo: {
      desempenhoQuantitativo: 'modesto',
      periodoMeses: 36,
      rankingPosicao: 18,
      rankingTotal: 100,
      capacidadeFiduciaria: 'modesta',
      garantiaConflitos: 'razoável',
      atendimentoRegulamento: 'razoavelmente'
    }
  }
};

export const obterConjuntoMock = pIsin => {
  return wConjuntosMock[String(pIsin)] || wConjuntosMock.generico;
};

const calcularNotasAvaliacao = (pAvaliacao, pNotasFallback = wConjuntosMock.generico.notas) => {
  const xNotaQualitativa = Number(pAvaliacao?.nota_qualitativa ?? pNotasFallback.notaQualitativa);
  const xNotaQuantitativa = Number(
    pAvaliacao?.nota_quantitativa ?? pNotasFallback.notaQuantitativa
  );
  const xNotaGeralContrato = pAvaliacao?.nota_geral;
  const xNotaGeral =
    xNotaGeralContrato !== null && xNotaGeralContrato !== undefined
      ? Number(xNotaGeralContrato)
      : xNotaQualitativa * 0.7 + xNotaQuantitativa * 0.3;

  return {
    notaGeral: Number.isFinite(xNotaGeral) ? xNotaGeral : 0,
    notaQualitativa: Number.isFinite(xNotaQualitativa) ? xNotaQualitativa : 0,
    notaQuantitativa: Number.isFinite(xNotaQuantitativa) ? xNotaQuantitativa : 0
  };
};

const AvaliacoesDataGrid = props => {
  const {
    state,
    output_id,
    onEditClick,
    onViewClick,
    onBulkDeleteClick,
    onDeleteClick,
    onSwitchChange,
    renderDetailPanel,
    data,
    ...restProps
  } = props;
  const { usuarioPrioridade } = usePerfil();
  const columns = useMemo(
    () => [
      {
        accessorKey: 'gestor',
        header: 'Gestor',
        enableColumnActions: false,
        enableSorting: false,
        // muiTableHeadCellProps: {
        //   sx: {
        //     width: '100% !important'
        //   }
        // },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (validators.isNull(value)) {
            return;
          }
          if (typeof value === 'object') {
            return value.pessoa_fantasia || value.pessoa || '';
          }
          return value;
        }
      },
      {
        id: 'fundo',
        accessorKey: 'ativo.ativo_seuid',
        header: 'Fundo',
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          if (validators.isEmpty(value)) {
            return;
          }
          return helpers.getAtivoDescricao(row.original.ativo);
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return (
            <Chip
              size="small"
              label={getAvaliacaoStatusDescricao(cell.getValue())}
              color={getAvaliacaoStatusColor(cell.getValue())}
            />
          );
        }
      },
      {
        id: 'nota_geral',
        accessorKey: 'nota_geral',
        header: 'Nota',
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({ cell, row }) => {
          // if (validators.isNull(cell.getValue())) {
          //   return;
          // }

          if (!isAvaliacaoDoneStatus(row?.original?.status)) {
            return;
          }

          const xIsin = row?.original?.ativo?.isin;

          if (validators.isNull(xIsin)) {
            return;
          }

          const xConjuntoMock = obterConjuntoMock(xIsin);
          const xNotas = calcularNotasAvaliacao(row.original, xConjuntoMock.notas);

          return (
            <Chip
              size="small"
              label={formats.formatNumber(xNotas.notaGeral, 2)}
              color={
                xNotas.notaGeral < 1.8
                  ? 'error'
                  : xNotas.notaGeral < 2.2
                  ? 'warning'
                  : xNotas.notaGeral < 3
                  ? 'info'
                  : 'primary'
              }
            />
          );
        }
      },
      {
        id: 'nota_qualitativa',
        accessorKey: 'nota_qualitativa',
        header: 'Nota Quali',
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({ cell, row }) => {
          // if (validators.isNull(cell.getValue())) {
          //   return;
          // }

          if (!isAvaliacaoDoneStatus(row?.original?.status)) {
            return;
          }

          const xIsin = row?.original?.ativo?.isin;

          if (validators.isNull(xIsin)) {
            return;
          }

          const xConjuntoMock = obterConjuntoMock(xIsin);
          const xNotas = calcularNotasAvaliacao(row.original, xConjuntoMock.notas);

          return (
            <Chip
              size="small"
              label={formats.formatNumber(xNotas.notaQualitativa, 2)}
              color={
                xNotas.notaQualitativa < 1.8
                  ? 'error'
                  : xNotas.notaQualitativa < 2.2
                  ? 'warning'
                  : xNotas.notaQualitativa < 3
                  ? 'info'
                  : 'primary'
              }
            />
          );
        }
      },
      {
        id: 'nota_quantitativa',
        accessorKey: 'nota_quantitativa',
        header: 'Nota Quanti',
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({ cell, row }) => {
          // if (validators.isNull(cell.getValue())) {
          //   return;
          // }

          if (!isAvaliacaoDoneStatus(row?.original?.status)) {
            return;
          }

          const xIsin = row?.original?.ativo?.isin;

          if (validators.isNull(xIsin)) {
            return;
          }

          const xConjuntoMock = obterConjuntoMock(xIsin);
          const xNotas = calcularNotasAvaliacao(row.original, xConjuntoMock.notas);

          return (
            <Chip
              size="small"
              label={formats.formatNumber(xNotas.notaQuantitativa, 2)}
              color={
                xNotas.notaQuantitativa < 1.8
                  ? 'error'
                  : xNotas.notaQuantitativa < 2.2
                  ? 'warning'
                  : xNotas.notaQuantitativa < 3
                  ? 'info'
                  : 'primary'
              }
            />
          );
        }
      },
      {
        id: 'modified',
        accessorKey: 'modified',
        header: 'Últ. Modificação',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        }
      },
      // {
      //   id: 'processada_em',
      //   accessorKey: 'processada_em',
      //   header: 'Processada',
      //   enableColumnActions: false,
      //   enableSorting: false,
      //   size: 100,
      //   Cell: ({ cell }) => {
      //     if (validators.isNull(cell.getValue())) {
      //       return;
      //     }
      //     return formats.formatDate(dates.toDate(cell.getValue()));
      //   }
      // },
      {
        id: 'reenvio',
        accessorKey: 'status',
        header: 'Reenvio',
        enableColumnActions: false,
        enableSorting: false,
        size: 100,
        Cell: ({ cell, row }) => {
          if (
            isAvaliacaoRascunhoStatus(cell.getValue()) ||
            isAvaliacaoWaitingStatus(cell.getValue())
          ) {
            return <ResendButton data={row.original} />;
          }
          return null;
        }
      }
    ],
    []
  );
  const xProps = {
    columns,
    data,
    state,
    enableRowSelection: usuarioPrioridade <= 6,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: ''
      }
    },
    renderTopToolbarCustomActions: ({ table }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }
      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;
      return (
        <BulkContextActions
          onDeleteClick={() => {
            onBulkDeleteClick(selectedRows);
          }}
          disabled={state.isLoading || state.showProgressBars || !isSomeRowSelected}
        />
      );
    },
    renderRowActions: ({ row }) => {
      return (
        <Stack direction="row" spacing={2}>
          <Tooltip title="Revisar Avaliação">
            <IconButton
              color="primary"
              onClick={() => onEditClick(row.original)}
              disabled={!canEditAvaliacao(row.original.status)}>
              <Icon iconName="edit" size={21} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Visualizar Avaliação">
            <IconButton
              color="primary"
              onClick={() => onViewClick(row.original)}
              disabled={!canViewAvaliacao(row.original.status)}>
              <Icon iconName="eye" size={21} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    },
    ...restProps
  };
  return <DataTable {...xProps} />;
};
AvaliacoesDataGrid.propTypes = {
  data: PropTypes.array.isRequired,
  state: PropTypes.object,
  initialState: PropTypes.object
};
export default AvaliacoesDataGrid;
